
import { useState, useEffect } from 'react'

interface Review {
  id: string
  rating: number
  comment: string
  created_at: string
  users: {
    first_name: string
    last_name: string
    avatar_url: string
  }
}

interface ReviewStats {
  average: number
  total: number
  distribution: { [key: number]: number }
}

interface ReviewsSectionProps {
  coachId: string
}

export default function ReviewsSection({ coachId }: ReviewsSectionProps) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [stats, setStats] = useState<ReviewStats>({
    average: 0,
    total: 0,
    distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  })
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    fetchReviews()
  }, [coachId])

  const fetchReviews = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-coach-reviews?coachId=${coachId}&limit=${showAll ? 50 : 3}`
      )
      
      const data = await response.json()
      
      if (response.ok) {
        setReviews(data.reviews || [])
        setStats(data.stats || { average: 0, total: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } })
      }
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const renderStars = (rating: number, size: string = 'text-sm') => {
    return (
      <div className={`flex items-center ${size}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`ri-star-${star <= rating ? 'fill' : 'line'} ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          ></i>
        ))}
      </div>
    )
  }

  const getDistributionPercentage = (count: number) => {
    return stats.total > 0 ? Math.round((count / stats.total) * 100) : 0
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">
        Avis et évaluations
      </h3>

      {stats.total > 0 ? (
        <>
          {/* Résumé des notes */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Note moyenne */}
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {stats.average.toFixed(1)}
              </div>
              {renderStars(Math.round(stats.average), 'text-lg')}
              <div className="text-sm text-gray-600 mt-2">
                Basé sur {stats.total} avis
              </div>
            </div>

            {/* Distribution des notes */}
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <div key={rating} className="flex items-center text-sm">
                  <span className="w-8 text-gray-600">{rating}</span>
                  <i className="ri-star-fill text-yellow-400 mx-1"></i>
                  <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${getDistributionPercentage(stats.distribution[rating])}%` }}
                    ></div>
                  </div>
                  <span className="w-8 text-gray-600 text-right">
                    {stats.distribution[rating]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Liste des avis */}
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="border-b border-gray-100 pb-6 last:border-b-0">
                <div className="flex items-start space-x-4">
                  <img
                    src={review.users.avatar_url}
                    alt={`${review.users.first_name} ${review.users.last_name}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {review.users.first_name} {review.users.last_name}
                        </h4>
                        <div className="flex items-center space-x-2">
                          {renderStars(review.rating)}
                          <span className="text-sm text-gray-500">
                            {formatDate(review.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {review.comment}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bouton pour voir plus d'avis */}
          {stats.total > 3 && !showAll && (
            <div className="text-center mt-6">
              <button
                onClick={() => {
                  setShowAll(true)
                  fetchReviews()
                }}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
              >
                Voir tous les avis ({stats.total})
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <i className="ri-star-line text-4xl text-gray-300 mb-4"></i>
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Aucun avis pour le moment
          </h4>
          <p className="text-gray-600">
            Soyez le premier à laisser un avis sur ce coach !
          </p>
        </div>
      )}
    </div>
  )
}
